import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PendingApproval from "@/pages/PendingApproval";
import Dashboard from "@/pages/Dashboard";
import Installers from "@/pages/Installers";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import Certificates from "@/pages/Certificates";
import CreateCertificate from "@/pages/CreateCertificate";
import InstallationDetail from "@/pages/InstallationDetail";
import ActivateBottle from "@/pages/ActivateBottle";
import ActivationHistory from "@/pages/ActivationHistory";
import CreditHistory from "@/pages/CreditHistory";
import Reports from "@/pages/Reports";
import FollowUps from "@/pages/FollowUps";
import FollowUpDetail from "@/pages/FollowUpDetail";
import Verify from "@/pages/Verify";
import Sessions from "@/pages/Sessions";
import AuditLog from "@/pages/AuditLog";
import Backups from "@/pages/Backups";
import Help from "@/pages/Help";
import About from "@/pages/About";
import Broadcast from "@/pages/Broadcast";
import BottleBatches from "@/pages/BottleBatches";
import ResourceCenter from "@/pages/ResourceCenter";
import ResourceDetail from "@/pages/ResourceDetail";
import ResourceAdmin from "@/pages/ResourceAdmin";
import ProductCodes from "@/pages/ProductCodes";
import ErrorPage from "@/pages/ErrorPage";
import { Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("App error:", error, info); }
  render() {
    if (this.state.hasError) return <ErrorPage code={500} />;
    return this.props.children;
  }
}
import AdminInstallerDetail from "@/pages/AdminInstallerDetail";
import EmailTemplates from "@/pages/EmailTemplates";

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === "installer" && user.status !== "active") return <Navigate to="/pending-approval" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pending-approval" element={<PendingApproval />} />

      {/* Public certificate verification */}
      <Route path="/verify" element={<Verify />} />
      <Route path="/verify/:certNumber" element={<Verify />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* Installer modules */}
      <Route path="/installations" element={<Navigate to="/certificates" replace />} />
      <Route path="/bottle-activations" element={<ProtectedRoute><ActivateBottle /></ProtectedRoute>} />
      <Route path="/activations" element={<ProtectedRoute><ActivationHistory /></ProtectedRoute>} />
      <Route path="/credit-history" element={<ProtectedRoute><CreditHistory /></ProtectedRoute>} />
      <Route path="/follow-ups" element={<ProtectedRoute><FollowUps /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
      <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
      <Route path="/follow-ups/:id" element={<ProtectedRoute><FollowUpDetail /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      {/* Shared */}
      <Route path="/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
      <Route path="/certificates/new" element={<ProtectedRoute><CreateCertificate /></ProtectedRoute>} />
      <Route path="/certificates/:id" element={<ProtectedRoute><InstallationDetail /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute><ResourceCenter /></ProtectedRoute>} />
      <Route path="/resources/:id" element={<ProtectedRoute><ResourceDetail /></ProtectedRoute>} />
      <Route path="/admin/resources" element={<ProtectedRoute requireAdmin><ResourceAdmin /></ProtectedRoute>} />

      {/* Admin only */}
      <Route path="/installers" element={<ProtectedRoute requireAdmin><Installers /></ProtectedRoute>} />
      <Route path="/installers/:id" element={<ProtectedRoute requireAdmin><AdminInstallerDetail /></ProtectedRoute>} />
      <Route path="/bottle-batches" element={<ProtectedRoute requireAdmin><BottleBatches /></ProtectedRoute>} />
      <Route path="/product-codes" element={<ProtectedRoute requireAdmin><ProductCodes /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute requireAdmin><Reports /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute requireAdmin><Users /></ProtectedRoute>} />
      <Route path="/sessions" element={<ProtectedRoute requireAdmin><Sessions /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute requireAdmin><AuditLog /></ProtectedRoute>} />
      <Route path="/backups" element={<ProtectedRoute requireAdmin><Backups /></ProtectedRoute>} />
      <Route path="/broadcast" element={<ProtectedRoute requireAdmin><Broadcast /></ProtectedRoute>} />
      <Route path="/email-templates" element={<ProtectedRoute requireAdmin><EmailTemplates /></ProtectedRoute>} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<ErrorPage code={404} />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" theme="dark" richColors />
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
